import * as d3 from 'd3';
import { D3ZoomEvent } from 'd3-zoom';
import { Collapse } from '../../../libs/gin-tonic/components/collapse/index';
import { Component } from '../../../libs/gin-tonic/helpers/compiler/index';
import { isDescendant } from '../../../libs/gin-tonic/helpers/dom/is-descendant/index';
import { debounce } from '../../../libs/gin-tonic/helpers/reactive/debounce/index';
import { hasClass } from '../../../libs/gin-tonic/helpers/dom/has-class/index';
import { removeClass } from '../../../libs/gin-tonic/helpers/dom/remove-class/index';

@Component({ className: 'js-interactive-map' })
export class InteractiveMapComponent {
	private svg: any;
	private activeLevel: SVGGraphicsElement | undefined;
	private g: any;
	private zoom: any;
	private classActive: string;
	private infoDetailClass: string;
	private bullets: Array<Element>;
	private navigationLevels: HTMLElement[];
	private navigationDivs: HTMLElement[];
	private checkBoxes: HTMLInputElement[];
	private areas: HTMLElement[];
	private areasCollapse: HTMLElement[];
	private area: HTMLElement[];
	private leaveDetailPageBtn: HTMLElement[];
	private svgAreas: SVGAElement[];
	private tooltipIcons: SVGElement[] | undefined;
	private dataIdAttribute: string;
	private svgIdAttribute: string;
	private svgWrapper: string;
	private svgWrapperElement: SVGGElement;
	private svgLayer: string;
	private svgContainer: any;
	private detailPage: HTMLElement;
	private currentView: HTMLElement;
	private currentLevel: HTMLElement;
	private isInitialLoad = true;

	constructor(private element: HTMLElement) {
		this.init();
	}

	private init() {
		this.navigationDivs = Array.from(this.element.getElementsByClassName('js-interactive-map-navigation')) as HTMLElement[];
		this.addActiveClassToNavigation(this.navigationDivs);
		this.currentView = document.querySelector('.js-interactive-map-navigation.active') as HTMLElement;
		this.currentLevel = document.querySelector('.js-interactive-map-navigation.active .js-interactive-map-level.active') as HTMLElement;

		this.bullets = Array.from(this.element.getElementsByClassName('js-interactive-map-bullet'));
		this.navigationLevels = Array.from(this.currentView.getElementsByClassName('js-interactive-map-level')) as HTMLElement[];
		this.checkBoxes = Array.from(this.currentView.getElementsByClassName('js-interactive-map-checkbox')) as HTMLInputElement[];
		this.areas = Array.from(this.element.getElementsByClassName('js-interactive-map-areas')) as HTMLElement[];
		this.areasCollapse = Array.from(this.element.getElementsByClassName('js-interactive-map-areas-collapse')) as HTMLElement[];
		this.area = Array.from(this.element.getElementsByClassName('js-interactive-map-area')) as HTMLElement[];
		this.leaveDetailPageBtn = Array.from(this.element.getElementsByClassName('js-interactive-map-close-detailpage')) as HTMLElement[];

		this.classActive = 'active';
		const levelClass = 'level';
		const prefixLevel = 'level-';
		const infoClass = 'info';
		const prefixTooltip = 'tooltip-';
		const prefixInfo = 'infos-';
		this.infoDetailClass = 'infodetail';
		const prefixInfoDetail = 'infodetail-';
		this.svgAreas = Array.from(this.element.querySelectorAll(`[id^="${prefixInfoDetail}"]`)) as SVGAElement[];
		this.dataIdAttribute = 'data-svg-id';
		this.svgIdAttribute = 'id';
		this.svgWrapper = '#svg-wrapper';
		this.svgLayer = '#Ebenen';
		this.svgContainer = document.querySelector('.c-interactive-map__container');
		this.tooltipIcons = Array.from(this.element.querySelectorAll(`[id^="${prefixTooltip}"]`));
		this.detailPage = document.querySelector('.c-interactive-map__page-detail') as HTMLElement;

		this.zoom = d3
			.zoom()
			.scaleExtent([1, 160])
			.on('zoom', (event: { transform: string }) => this.zoomed(event));

		this.svg = d3
			.select(this.svgLayer)
			.attr('viewBox', [0, 0, window.innerWidth, window.innerHeight])
			.attr('width', window.innerWidth)
			.attr('height', window.innerHeight)
			.attr('style', 'max-width: 100%; height: auto;');

		const x = d3.select(window).on(
			'resize.updatesvg',
			debounce(() => this.updateWindow(), 100)
		);

		this.g = this.svg.select(this.svgWrapper);
		this.svgWrapperElement = document.querySelector(this.svgWrapper) as any as SVGGElement;

		const params = new URLSearchParams(window.location.search);
		const levelParam = params.get('level');
		if (params && levelParam && document.getElementById(levelParam!)) {
			this.activeLevel = document.getElementById(levelParam) as any as SVGGraphicsElement;
		}

		const levels = this.element.querySelectorAll(`[id^="${prefixLevel}"]`);
		if (levels && levels.length > 0) {
			this.activeLevel = this.activeLevel || (document.getElementById(levels[0].id) as any as SVGGraphicsElement);
			levels.forEach(element => {
				element.classList.add(levelClass);
			});
		}

		if (this.navigationLevels && this.navigationLevels.length > 0) {
			this.activeLevel = this.activeLevel || (document.getElementById(this.navigationLevels[0].id) as any as SVGGraphicsElement);
			this.navigationLevels.forEach(element => {
				new Collapse(element);
			});
		}

		const infos = this.element.querySelectorAll(`[id^="${prefixInfo}"]`);
		if (infos.length > 0) {
			infos.forEach(element => {
				element.classList.add(infoClass);
			});
		}

		const infoDetails = this.element.querySelectorAll(`[id^="${prefixInfoDetail}"]`);
		if (infoDetails.length > 0) {
			infoDetails.forEach(element => {
				element.classList.add(this.infoDetailClass);
			});
		}

		this.setupButtonListeners();
		this.setupNavigationListeners();
		this.setupCheckboxListeners(params);
		this.setupAreasListeners(params);
		this.setupAreaListeners(this.area, this.dataIdAttribute, params);
		this.setupAreaListeners(this.svgAreas, this.svgIdAttribute, params);
		this.setupTooltipListeners();
		this.setupCloseDetailPageListener();

		const activeArea = this.area.find(o => o.classList.contains('active'));
		if (activeArea) {
			const activeAreaId = activeArea.getAttribute(this.dataIdAttribute);
			if (activeAreaId) {
				const targetSvgElement = document.getElementById(activeAreaId);
				if (targetSvgElement && targetSvgElement instanceof SVGGraphicsElement) {
					this.element.querySelector(`[id^="${prefixLevel}aargau"]`)?.classList.add('active');
				}
			}
		} else {
			if (this.activeLevel) {
				this.updateActiveLevel(this.activeLevel, true);
			}
		}

		this.svg.call(this.zoom);
		this.isInitialLoad = false;
	}

	private updateWindow() {
		d3.select(this.svgLayer)
			.attr('viewBox', [0, 0, window.innerWidth, window.innerHeight])
			.attr('width', window.innerWidth)
			.attr('height', window.innerHeight);
		if (this.activeLevel) {
			this.zoomTo(this.activeLevel, false);
		}
		this.init();
	}

	private addActiveClassToNavigation(navigationDivs: HTMLElement[]) {
		navigationDivs.forEach(div => {
			if (div && div instanceof HTMLElement) {
				const style = window.getComputedStyle(div);
				if (style.display === 'none') {
					div.classList.remove('active');
				} else {
					div.classList.add('active');
				}
			}
		});
	}

	private updateUrl(pageReload: boolean) {
		const params = new URLSearchParams();
		if (this.activeLevel) {
			params.append('level', this.activeLevel.id);
		}

		const level = this.navigationLevels.find(o => o.getAttribute(this.dataIdAttribute) == this.activeLevel?.id);

		if (level) {
			const layers = this.checkBoxes.filter(o => o.checked && isDescendant(level, o)).map(o => o.value);
			if (layers && layers.length > 0) {
				layers.forEach(layer => {
					params.append('layers', layer);
				});
			}

			const areas = this.areas
				.filter(o => o.getAttribute(this.dataIdAttribute) && o.Collapse?.isOpen() && isDescendant(level, o))
				.map(o => o.getAttribute(this.dataIdAttribute));
			if (areas && areas.length > 0) {
				areas.forEach(area => {
					params.append('areas', area!);
				});
			}
		}

		const area = this.area.filter(o => o.getAttribute(this.dataIdAttribute) && hasClass(o, 'active')).map(o => o.getAttribute(this.dataIdAttribute));

		if (area && area.length > 0) {
			params.append('area', area);
		}

		let url = `${window.location.pathname}`;
		if (params && params.size > 0) {
			url += `?${params.toString()}`;
		}
		history.pushState(null, document.title, url);

		if (pageReload) {
			window.location.href = url;
		}
	}

	setupCloseDetailPageListener(): void {
		if (this.leaveDetailPageBtn) {
			this.leaveDetailPageBtn.forEach(backElement => {
				backElement.addEventListener('click', () => {
					this.removeActiveClassDetail();
					const targetLevelId = backElement.getAttribute(this.dataIdAttribute);

					if (targetLevelId) {
						const targetLevel = document.getElementById(targetLevelId);
						const targetNavLevel = Array.from(this.navigationLevels).find(
							navLevel => navLevel.getAttribute(this.dataIdAttribute) === targetLevelId
						);
						if (targetLevel && targetLevel instanceof SVGGraphicsElement && targetNavLevel) {
							this.updateActiveLevel(targetLevel);
						}
					}

					this.removeActiveClassArea();
					this.removeActiveClassAreaSvg();
					this.updateUrl(false);
				});
			});
		}
	}

	removeActiveClassArea() {
		const activeArea = document.querySelector('.is-area-active');
		if (activeArea) {
			activeArea.classList.remove('is-area-active');
		}

		document.querySelector<HTMLElement>('.c-interactive-map__accordion__item__head__title.active')?.classList.remove('active');
	}

	removeActiveClassAreaSvg() {
		const infoDetails = document.querySelectorAll(`[id^="infodetail"]`);
		if (infoDetails.length > 0) {
			infoDetails.forEach(element => {
				element.classList.remove('active');
			});
		}
	}

	removeActiveClassDetail() {
		document.querySelector<HTMLElement>('.c-interactive-map__page-detail__wrapper.active')?.classList.remove('active');
	}

	setupButtonListeners(): void {
		if (this.bullets != null) {
			this.bullets.forEach(button => {
				button.addEventListener('click', event => {
					const targetLevelId = (event.target as HTMLElement).getAttribute(this.dataIdAttribute);
					if (targetLevelId) {
						const targetLevel = document.getElementById(targetLevelId);
						if (targetLevel instanceof SVGGraphicsElement) {
							const collapseNav = document.querySelector('.js-interactive-map-level.active .is-active');
							if (collapseNav) {
								this.updateActiveLevel(targetLevel, false, true);
							} else {
								this.updateActiveLevel(targetLevel, false, false);
							}
						}
					}
				});
			});
		}
	}

	setupNavigationListeners(): void {
		this.navigationLevels.forEach(level => {
			const levelBack = level.querySelector('.js-interactive-map-back') as HTMLElement;
			const levelForward = level.querySelector('.js-interactive-map-forward') as HTMLElement;

			const handleNavigation = (element: HTMLElement) => {
				const targetLevelId = element.getAttribute(this.dataIdAttribute);

				if (targetLevelId) {
					const targetLevel = document.getElementById(targetLevelId);
					const targetNavLevel = Array.from(this.navigationLevels).find(navLevel => navLevel.getAttribute(this.dataIdAttribute) === targetLevelId);
					if (targetLevel && targetLevel instanceof SVGGraphicsElement && targetNavLevel) {
						this.updateActiveLevel(targetLevel);
					}
				}
			};

			if (levelBack) {
				levelBack.addEventListener('click', () => handleNavigation(levelBack));
			}

			if (levelForward) {
				levelForward.addEventListener('click', () => handleNavigation(levelForward));
			}
		});
	}

	setupCheckboxListeners(params: URLSearchParams): void {
		if (this.checkBoxes != null) {
			const layersParam = params.getAll('layers');
			this.checkBoxes.forEach(checkbox => {
				checkbox.addEventListener('change', event => {
					const targetInfosId = checkbox.getAttribute(this.dataIdAttribute);
					if (targetInfosId) {
						const targetInfo = document.getElementById(targetInfosId);
						targetInfo?.classList.toggle(this.classActive);
					}
					const level = this.navigationLevels.find(o => o.getAttribute(this.dataIdAttribute) == this.activeLevel?.id);
					if (level && level.classList.contains('is-areas-active')) {
						const areas = level ? this.areas.filter(o => isDescendant(level, o)) : undefined;
						const firstOpenArea = areas?.find(o => o.Collapse?.isOpen()) || (areas ? areas[0] : null);
						if (firstOpenArea) {
							firstOpenArea.Collapse?.hide();
						}
					}
					this.updateUrl(false);
				});
				let checked = checkbox.checked;
				if (layersParam && layersParam.length > 0) {
					checked = layersParam.some(o => o == checkbox.value);
					checkbox.checked = checked;
				}
				const targetInfosId = checkbox.getAttribute(this.dataIdAttribute);
				if (targetInfosId) {
					const targetInfo = document.getElementById(targetInfosId);
					targetInfo?.classList.toggle(this.classActive, checked);
				}
			});
		}
	}

	handleCollapseEvent(areaOverview: HTMLElement, isAreasActive: boolean) {
		const level = this.navigationLevels.find(o => o.getAttribute(this.dataIdAttribute) == this.activeLevel?.id);
		const checkboxes = level ? this.checkBoxes.filter(o => isDescendant(level, o)) : undefined;
		const targetInfosId = areaOverview.getAttribute(this.dataIdAttribute);
		if (isAreasActive) {
			level?.classList.add('is-areas-active');
		} else {
			level?.classList.remove('is-areas-active');
		}

		if (targetInfosId) {
			const targetInfo = document.getElementById(targetInfosId);
			if (isAreasActive) {
				targetInfo?.classList.add(this.classActive);
			} else {
				targetInfo?.classList.remove(this.classActive);
			}
		}
		if (checkboxes && checkboxes.length > 0) {
			checkboxes.forEach(checkbox => {
				const targetInfosId = checkbox.getAttribute(this.dataIdAttribute);
				if (targetInfosId) {
					const targetInfo = document.getElementById(targetInfosId);
					if (isAreasActive) {
						targetInfo?.classList.remove(this.classActive);
					} else {
						targetInfo?.classList.toggle(this.classActive, checkbox.checked);
					}
				}
			});
		}
	}

	setupAreasListeners(params: URLSearchParams): void {
		if (this.areas != null) {
			const areasParam = params.getAll('areas');

			this.areas.forEach(areaOverview => {
				areaOverview.addEventListener('hide.opten.collapse', () => {
					this.handleCollapseEvent(areaOverview, false);
				});
				areaOverview.addEventListener('show.opten.collapse', () => {
					this.handleCollapseEvent(areaOverview, true);
				});
				areaOverview.addEventListener('hidden.opten.collapse', () => {
					this.updateUrl(false);
				});
				areaOverview.addEventListener('shown.opten.collapse', () => {
					this.updateUrl(false);
				});
				if (
					areasParam &&
					areasParam.length > 0 &&
					areaOverview.getAttribute(this.dataIdAttribute) &&
					areasParam.indexOf(areaOverview.getAttribute(this.dataIdAttribute)!) != -1
				) {
					const collapse = Collapse.getInstance(areaOverview);
					if (collapse) {
						collapse.show();
					}
				}
			});

			this.areasCollapse.forEach(areaAccordionOpener => {
				areaAccordionOpener.addEventListener('show.opten.collapse', () => {
					const accordion = areaAccordionOpener
						.closest('.js-interactive-map-level')
						?.querySelector('.c-interactive-map__navigation__main') as HTMLElement;
					if (accordion) {
						const collapse = Collapse.getInstance(accordion);
						if (collapse) {
							collapse.hide();
						}
					}
				});
			});
		}
	}

	setupAreaListeners(elements: Array<Element>, idAttribute: string, params: URLSearchParams): void {
		if (elements != null) {
			const areaParam = params.getAll('area');

			elements.forEach(child => {
				const thisChild = child;
				const targetSVG = child.getAttribute(this.dataIdAttribute) || child.id;

				if (targetSVG) {
					const targetSvgElement = document.getElementById(targetSVG);

					if (targetSvgElement && targetSvgElement instanceof SVGGraphicsElement) {
						// setzt parameter in url falls direkt aufgerufen
						if (
							areaParam &&
							areaParam.length > 0 &&
							child.getAttribute(this.dataIdAttribute) &&
							areaParam.indexOf(child.getAttribute(this.dataIdAttribute)!) != -1
						) {
							this.updateActiveArea(targetSvgElement, params);
						}

						child.addEventListener('click', event => {
							const isAreasActive = targetSvgElement.classList.contains('active');

							if (isAreasActive) {
								this.removeActiveClassDetail();
								const targetLevelId = targetSvgElement.closest('.level.active');

								if (targetLevelId) {
									const targetLevel = targetLevelId;
									if (targetLevel && targetLevel instanceof SVGGraphicsElement) {
										this.updateActiveLevel(targetLevel);
									}
								}

								this.removeActiveClassArea();
								this.removeActiveClassAreaSvg();
							} else {
								if (this.detailPage) {
									Array.from(this.detailPage.children).forEach(child => {
										if (child.getAttribute(this.dataIdAttribute) == thisChild.getAttribute(this.dataIdAttribute)) {
											child.classList.add(this.classActive);
										}
									});
								}

								this.updateActiveArea(targetSvgElement, params);
							}
						});
					}
				}
			});
		}
	}

	setupTooltipListeners(): void {
		if (this.tooltipIcons != null) {
			const addActiveClass = (event: Event) => {
				if ((event.target as HTMLElement).closest(`[id^="tooltipdetail-"]`)) {
					return;
				}
				const target = event.currentTarget as HTMLElement;
				target.classList.add('active');
			};

			const removeActiveClass = (event: Event) => {
				if ((event.target as HTMLElement).closest(`[id^="tooltipdetail-"]`)) {
					return;
				}
				const target = event.currentTarget as HTMLElement;
				target.classList.remove('active');
			};

			this.tooltipIcons.forEach(tooltipIcon => {
				tooltipIcon.addEventListener('mouseenter', addActiveClass);
				tooltipIcon.addEventListener('mouseleave', removeActiveClass);

				tooltipIcon.addEventListener('touchstart', addActiveClass);
				tooltipIcon.addEventListener('touchend', removeActiveClass);
			});
		}
	}

	updateActiveLevel(newActiveLevel: SVGGElement, ignoreUpdateUrl = false, doPageReload = false): void {
		const removeSelectors = `[${this.dataIdAttribute}="${this.activeLevel?.id}"].${this.classActive}, [id="${this.activeLevel?.id}"].${this.classActive}`;
		this.element.querySelectorAll(removeSelectors).forEach(element => {
			element.classList.remove(this.classActive);
		});

		this.activeLevel = newActiveLevel;

		const addSelectors = `[${this.dataIdAttribute}="${this.activeLevel.id}"], [id="${this.activeLevel.id}"]`;
		this.element.querySelectorAll(addSelectors).forEach(element => {
			element.classList.add(this.classActive);
		});

		this.navigationLevels.forEach(element => {
			const id = element.getAttribute(this.dataIdAttribute);
			if (id) {
				if (element.classList.contains(this.classActive)) {
					Collapse.getInstance(element).show();
				} else {
					Collapse.getInstance(element).hide();
				}
			}
		});

		if (newActiveLevel.classList.contains(this.infoDetailClass)) {
			this.svgContainer.classList.add(this.classActive);
			const level = this.navigationLevels.find(o => o.getAttribute(this.dataIdAttribute) == 'level-aargau');

			level?.classList.add('is-areas-active');
		}

		this.zoomTo(newActiveLevel, false);
		if (!ignoreUpdateUrl) {
			this.updateUrl(doPageReload);
		}
	}

	updateActiveArea(newActiveArea: SVGGElement, params: URLSearchParams): void {
		const addSelectors = `[${this.dataIdAttribute}="${newActiveArea.id}"], [id="${newActiveArea.id}"]`;
		this.element.querySelectorAll(addSelectors).forEach(element => {
			element.classList.add(this.classActive);
		});

		this.navigationLevels.forEach(level => {
			if (level) {
				const collapse = Collapse.getInstance(level);
				if (collapse) {
					collapse.hide();
				}
			}
		});

		newActiveArea.classList.add('is-area-active');

		const navItem = this.area.find(o => o.getAttribute(this.dataIdAttribute) == newActiveArea.getAttribute(this.svgIdAttribute));
		if (navItem) {
			navItem.classList.add(this.classActive);
		}

		const firstChild = newActiveArea.firstElementChild as SVGGElement;
		this.zoomTo(firstChild);
		this.updateUrl(false);
	}

	zoomTo(targetElement: SVGGElement, useTargetElementAsDefault: boolean = true) {
		var targetToZoom: SVGGElement = targetElement;
		if (useTargetElementAsDefault == false) {
			const defaultId = targetElement.getAttribute('id') + '-default';
			const targetDefaultElement = document.getElementById(defaultId);
			if (targetDefaultElement && targetDefaultElement instanceof SVGGraphicsElement) {
				targetToZoom = targetDefaultElement as unknown as SVGGElement;
			}
		}
		const [[x0, y0], [x1, y1]] = this.getBounds(targetToZoom);
		const targetToZoomBBox = targetToZoom.getBBox();
		const targetToZoomWidth = targetToZoomBBox.width;
		const targetToZoomHeight = targetToZoomBBox.height;
		const targetElementBBox = targetElement.getBBox();
		const targetElementWidth = targetElementBBox.width;
		const targetElementHeight = targetElementBBox.height;

		const scale = useTargetElementAsDefault
			? Math.min(window.innerWidth / targetToZoomWidth, window.innerHeight / targetToZoomHeight) * 0.4
			: Math.max(window.innerWidth / targetToZoomWidth, window.innerHeight / targetToZoomHeight);

		const minScale = useTargetElementAsDefault
		? Math.min(window.innerWidth / targetElementWidth, window.innerHeight / targetElementHeight) * 0.4
		: Math.max(window.innerWidth / targetElementWidth, window.innerHeight / targetElementHeight); // minimum scale to fit the content to the window
		const maxScale = 160; // maximum scale for zooming in

		const centerX = (x0 + x1) / 2;
		const centerY = (y0 + y1) / 2;
		const translateX = window.innerWidth / 2 - centerX * scale;
		const translateY = window.innerHeight / 2 - centerY * scale;

		const translateExtent = this.getBounds(targetElement);

		this.zoom.translateExtent(translateExtent).scaleExtent([minScale, maxScale]);
		const duration = this.isInitialLoad ? 0 : 750;

		this.svg.transition().duration(duration).call(this.zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
	}

	getBounds(element: SVGGraphicsElement): [[number, number], [number, number]] {
		try {
			const bbox = element.getBBox();
			const bounds: [[number, number], [number, number]] = [
				[bbox.x, bbox.y],
				[bbox.x + bbox.width, bbox.y + bbox.height]
			];
			return bounds;
		} catch (error) {
			console.error('Error calculating bounds:', error);
			return [
				[0, 0],
				[0, 0]
			];
		}
	}

	zoomed(event: D3ZoomEvent<SVGGElement, unknown>): void {
		this.g.attr('transform', event.transform);
	}
}
